<script>
import constants from '@/constants';
import { formatMinskDate } from '@/scripts/tools.js';

export default {
    props: {
        record: {
            type: Object,
            required: true,
        },
    },
    computed: {
        engagementTierData() {
            return (
                constants.ENGAGEMENT_TIERS.find(
                    (t) => t.value === this.record.engagement_tier
                ) || {}
            );
        },
        engagementTierLabel() {
            return this.engagementTierData.label || this.record.engagement_tier;
        },
        engagementTierHint() {
            return this.engagementTierData.hint || '';
        },
        recencyStatusData() {
            return (
                constants.RECENCY_STATUSES.find(
                    (s) => s.value === this.record.recency_status
                ) || {}
            );
        },
        recencyStatusLabel() {
            return this.recencyStatusData.label || this.record.recency_status;
        },
        recencyStatusHint() {
            return this.recencyStatusData.hint || '';
        },
    },
    methods: {
        formatDate(dateString) {
            return formatMinskDate(dateString, false);
        },
        truncateEmail(email, maxLength = 25) {
            if (!email) return 'N/A';
            if (email.length <= maxLength) return email;
            return email.substring(0, maxLength) + '...';
        },
        openUserEdit() {
            this.$router.push({
                name: constants.ROUTER_NAME.USERANALYTICSWITHUSER,
                params: { userId: this.record.user_id },
            });
        },
        filterByEngagement() {
            this.$store.commit('SET_USER_ANALYTICS_FILTERS', {
                ...this.$store.getters.USER_ANALYTICS_FILTERS,
                engagementTier: this.record.engagement_tier,
            });
            this.$store.commit('SET_USER_ANALYTICS_PAGE', 1);
            this.$store.dispatch('loadUserAnalytics');
        },
        filterByRecency() {
            this.$store.commit('SET_USER_ANALYTICS_FILTERS', {
                ...this.$store.getters.USER_ANALYTICS_FILTERS,
                recencyStatus: this.record.recency_status,
            });
            this.$store.commit('SET_USER_ANALYTICS_PAGE', 1);
            this.$store.dispatch('loadUserAnalytics');
        },
    },
};
</script>

<template>
    <tr>
        <td>{{ record.user_id }}</td>
        <td :title="record.user_email" class="email_cell">
            <a
                href="#"
                @click.prevent="openUserEdit"
                class="email_link"
                :aria-label="`View profile for ${record.user_email}`"
            >
                {{ truncateEmail(record.user_email) }}
            </a>
        </td>
        <td>
            <span
                class="status_badge"
                :class="
                    record.user_is_active ? 'status_active' : 'status_inactive'
                "
            >
                {{ record.user_is_active ? 'Active' : 'Inactive' }}
            </span>
        </td>
        <td class="td_number">{{ record.total_downloads.toLocaleString() }}</td>
        <td class="td_number">{{ record.unique_files_downloaded?.toLocaleString() || '0' }}</td>
        <td class="td_number">{{ record.collections_created?.toLocaleString() || '0' }}</td>
        <td class="td_number">{{ record.zip_downloads?.toLocaleString() || '0' }}</td>
        <td class="td_number">{{ record.total_active_days }}</td>
        <td>{{ formatDate(record.last_activity_date) }}</td>
        <td>
            <span
                class="tier_badge clickable_badge"
                :class="'tier_' + record.engagement_tier"
                :title="engagementTierHint"
                @click="filterByEngagement"
            >
                {{ engagementTierLabel }}
            </span>
        </td>
        <td>
            <span
                class="recency_badge clickable_badge"
                :class="'recency_' + record.recency_status"
                :title="recencyStatusHint"
                @click="filterByRecency"
            >
                {{ recencyStatusLabel }}
            </span>
        </td>
        <td>{{ record.current_plan_name || '−' }}</td>
    </tr>
</template>

<style scoped>
td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--border-color);
    color: var(--text-color);
    font-size: 14px;
}

.email_cell {
    cursor: pointer;
}

.email_link {
    color: #1e40af;
    text-decoration: none;
    transition: opacity 0.2s;
    font-weight: 500;
}

.email_link:hover {
    opacity: 0.7;
    text-decoration: underline;
}

.td_number {
    text-align: right;
    font-variant-numeric: tabular-nums;
}

.status_badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
}

.status_active {
    background: #d1fae5;
    color: #065f46;
}

.status_inactive {
    background: #f3f4f6;
    color: #6b7280;
}

/* Engagement Tier Badges */
.tier_badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
}

.tier_never_active {
    background: #f3f4f6;
    color: #6b7280;
}

.tier_one_day_visitor {
    background: #dbeafe;
    color: #1e40af;
}

.tier_casual_visitor {
    background: #bfdbfe;
    color: #1e40af;
}

.tier_weekly_user {
    background: #d1fae5;
    color: #065f46;
}

.tier_regular_user {
    background: #a7f3d0;
    color: #065f46;
}

.tier_power_user {
    background: #e9d5ff;
    color: #7c3aed;
}

/* Recency Status Badges */
.recency_badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
}

.recency_never_active {
    background: #f3f4f6;
    color: #6b7280;
}

.recency_active_this_week {
    background: #d1fae5;
    color: #065f46;
}

.recency_active_this_month {
    background: #fef3c7;
    color: #92400e;
}

.recency_dormant {
    background: #fed7aa;
    color: #c2410c;
}

.recency_churned {
    background: #fee2e2;
    color: #991b1b;
}

.clickable_badge {
    cursor: pointer;
    transition: opacity 0.2s, transform 0.1s;
}

.clickable_badge:hover {
    opacity: 0.85;
    transform: translateY(-1px);
}

.clickable_badge:active {
    transform: translateY(0);
}
</style>
